/* eslint-disable max-lines -- таблица замечаний: три секции (курс, уроки, экраны) в одной разметке */
import {
  langOf,
  type BlockIssue,
  type CourseFieldIssue,
  type LessonFieldIssue,
} from '@/features/editor/lib/publishIssues'
import { isBlocking } from '@/features/editor/lib/publishOverflowIssues'
import { GoButton, TD, TH, Table, useIssueNavigation, type IssuesTone } from './PublishIssuesParts'
import type { ViolationGo } from '../publish/violationPlace'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  fields: CourseFieldIssue[]
  lessons: LessonFieldIssue[]
  blocks: BlockIssue[]
  tone?: IssuesTone
  /** Свой переход (настройки курса: закрыть окно / сменить вкладку); иначе — в редакторе. */
  onGo?: (target: ViolationGo) => void
}

/** Что мешает публикации — таблицами (курс и блоки) с переходом к месту. */
export const PublishIssuesTable = ({ fields, lessons, blocks, tone = 'inverted', onGo }: Props) => {
  const m = useMessages(messages)
  const go = useIssueNavigation(onGo)
  // Одни рекомендации (жёлтое переполнение) — мягкий заголовок: публикации они не мешают.
  const blocking = isBlocking(fields.length + lessons.length, blocks)
  // Поле курса — открыть «Основное» сразу на языке этого поля (EN — английский).
  const openSettings = (lang: string) => go({ kind: 'course', lang: langOf(lang) })
  const openBlock = (b: BlockIssue) =>
    go(
      b.blockId === null
        ? { kind: 'lesson', lessonId: b.lessonId }
        : { kind: 'block', lessonId: b.lessonId, blockId: b.blockId, lang: langOf(b.lang) },
    )
  return (
    <div className="space-y-3 text-sm">
      <p className="font-semibold">{blocking ? m.issuesHeading : m.warningsHeading}</p>
      {fields.length > 0 ? (
        <Table
          tone={tone}
          head={
            <tr>
              <th className={TH}>{m.colCourse}</th>
              <th className={TH}>{m.colLang}</th>
              <th />
            </tr>
          }
        >
          {fields.map((f) => (
            <tr key={`${f.field}-${f.lang}`}>
              <td className={TD}>{f.field}</td>
              <td className={`${TD} tabular-nums`}>{f.lang}</td>
              <td className={`${TD} text-right`}>
                {/* «—» — не поле настроек (курс без уроков): вести некуда. */}
                {f.lang === '—' ? null : (
                  <GoButton
                    onClick={() => openSettings(f.lang)}
                    label={m.goToField(f.field, f.lang)}
                  />
                )}
              </td>
            </tr>
          ))}
        </Table>
      ) : null}
      {lessons.length > 0 ? (
        <Table
          tone={tone}
          head={
            <tr>
              <th className={TH}>{m.colLesson}</th>
              <th className={TH}>{m.colField}</th>
              <th className={TH}>{m.colLang}</th>
              <th />
            </tr>
          }
        >
          {lessons.map((l) => (
            <tr key={`${l.lessonId}-${l.field}-${l.lang}`}>
              <td className={`${TD} tabular-nums`}>{l.lesson}</td>
              <td className={TD}>{l.field}</td>
              <td className={`${TD} tabular-nums`}>{l.lang}</td>
              <td className={`${TD} text-right`}>
                <GoButton
                  // Урок открывается сразу на языке незаполненного поля.
                  onClick={() => go({ kind: 'lesson', lessonId: l.lessonId, lang: langOf(l.lang) })}
                  label={m.goToLesson(l.lesson)}
                />
              </td>
            </tr>
          ))}
        </Table>
      ) : null}
      {blocks.length > 0 ? (
        <Table
          tone={tone}
          head={
            <tr>
              <th className={TH}>{m.colLesson}</th>
              <th className={TH}>{m.colBlock}</th>
              <th className={TH}>{m.colProblem}</th>
              <th className={TH}>{m.colLang}</th>
              <th />
            </tr>
          }
        >
          {blocks.map((b) => (
            <tr key={`${b.lesson}-${b.screen}-${b.problem}-${b.lang}`}>
              <td className={`${TD} tabular-nums`}>{b.lesson}</td>
              <td className={`${TD} tabular-nums`}>{b.screen ?? '—'}</td>
              <td className={TD}>{b.problem}</td>
              <td className={`${TD} tabular-nums`}>{b.lang}</td>
              <td className={`${TD} text-right`}>
                <GoButton
                  onClick={() => openBlock(b)}
                  label={
                    b.screen === null ? m.goToLesson(b.lesson) : m.goToBlock(b.lesson, b.screen)
                  }
                />
              </td>
            </tr>
          ))}
        </Table>
      ) : null}
    </div>
  )
}
